import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, BarChart2, CheckCircle2, ExternalLink, Heart, PlusCircle, X } from "lucide-react";
import { supabase, goUrl } from "@/lib/supabase";
import { posthog } from "@/lib/posthog";
import { AppNav } from "@/components/AppNav";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { ListingDetailDrawer, type Listing } from "@/components/ListingDetailDrawer";
import { ComparadorPanel } from "@/components/ComparadorPanel";
import { VisitedBadge } from "@/components/VisitedBadge";
import { MercadoPanel } from "@/components/MercadoPanel";

const MERCADO_PANEL_ENABLED = true;

export const Route = createFileRoute("/alertas/$alertId")({
  head: () => ({
    meta: [
      { title: "Radar — Busca Blindados" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AlertInboxPage,
});

type AlertMeta = {
  id: string;
  name: string;
  model_filters: { slug: string; display_name?: string; keywords?: string }[] | null;
  min_year: number | null;
  max_year: number | null;
  max_km: number | null;
  min_price: number | null;
  max_price: number | null;
};

type FilterDispatch = {
  id: string;
  sent_at: string;
  new_count: number;
  gone_count: number;
  total_count: number;
};

const COMPARADOR_MAX = 4;
type SortKey = "source" | "year" | "km" | "price";
type SortDir = "asc" | "desc";

function fmtBR(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function sortListings<T extends Listing>(listings: T[], key: SortKey, dir: SortDir): T[] {
  return [...listings].sort((a, b) => {
    let av: string | number | null;
    let bv: string | number | null;
    if (key === "source") { av = a.source; bv = b.source; }
    else if (key === "year") { av = a.year; bv = b.year; }
    else if (key === "km") { av = a.km; bv = b.km; }
    else { av = a.price; bv = b.price; }

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === "asc" ? cmp : -cmp;
  });
}

function AlertInboxPage() {
  const { alertId } = Route.useParams();
  const { session, loading: sessionLoading } = useSession();
  const { isPaid, isFree, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionLoading || profileLoading) return;
    if (!session) { navigate({ to: "/login" }); return; }
    if (!isPaid && !isFree) { navigate({ to: "/planos" }); return; }
  }, [session, sessionLoading, isPaid, isFree, profileLoading, navigate]);

  const [alert, setAlert] = useState<AlertMeta | null>(null);
  const [listings, setListings] = useState<(Listing & { status: string })[] | null>(null);
  const [dispatches, setDispatches] = useState<FilterDispatch[] | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [comparadorItems, setComparadorItems] = useState<Listing[]>([]);
  const [comparadorOpen, setComparadorOpen] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("price");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  useEffect(() => {
    if (!session) return;

    Promise.all([
      supabase
        .from("filters")
        .select("id, name, model_filters, min_year, max_year, max_km, min_price, max_price")
        .eq("id", alertId)
        .is("deleted_at", null)
        .single(),
      supabase
        .from("filter_seen")
        .select(
          "listing_id, notified_at, listings(id, source, title, display_title, model_slug, base_family, year, km, price, url, status)",
        )
        .eq("filter_id", alertId)
        .order("notified_at", { ascending: false }),
      supabase
        .from("filter_dispatches")
        .select("id, sent_at, new_count, gone_count, total_count")
        .eq("filter_id", alertId)
        .order("sent_at", { ascending: false })
        .limit(30),
    ]).then(([alertRes, seenRes, dispatchRes]) => {
      if (alertRes.error || !alertRes.data) {
        setFetchError("Alerta não encontrado.");
        return;
      }
      setAlert(alertRes.data as AlertMeta);

      if (seenRes.error) {
        setFetchError("Não foi possível carregar os resultados.");
        return;
      }

      const mapped = (seenRes.data ?? [])
        .map((row) => row.listings as unknown as (Listing & { status: string }) | null)
        .filter((l): l is Listing & { status: string } => l !== null && l.status === "active");

      setListings(mapped);
      setDispatches((dispatchRes.data ?? []) as FilterDispatch[]);
    });
  }, [session, alertId]);

  const toggleComparador = (listing: Listing) => {
    setComparadorItems((prev) => {
      if (prev.some((l) => l.id === listing.id)) return prev.filter((l) => l.id !== listing.id);
      if (prev.length >= COMPARADOR_MAX) return prev;
      return [...prev, listing];
    });
  };

  useEffect(() => {
    if (!session || !listings || listings.length === 0) return;
    supabase
      .from("favorites")
      .select("listing_id")
      .in("listing_id", listings.map((l) => l.id))
      .eq("user_id", session.user.id)
      .then(({ data }) => {
        setFavoriteIds(new Set((data ?? []).map((r) => r.listing_id as string)));
      });
  }, [session?.user.id, listings]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = async (e: React.MouseEvent, l: Listing) => {
    e.stopPropagation();
    const isFav = favoriteIds.has(l.id);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      isFav ? next.delete(l.id) : next.add(l.id);
      return next;
    });
    if (isFav) {
      await supabase.from("favorites").delete()
        .eq("listing_id", l.id).eq("user_id", session!.user.id);
    } else {
      await supabase.from("favorites").insert({
        user_id: session!.user.id,
        listing_id: l.id,
        price_at_save: l.price,
      });
      posthog.capture("listing_favorited", { listing_id: l.id });
    }
  };

  const buscarUrl = alert ? (() => {
    const p = new URLSearchParams();
    // Prefer base_family from actual matched listings — model_filters.slug is a FIPE
    // trim slug (e.g. "volkswagen-t-cross-hig-250-tsi") which doesn't match base_family.
    const seenFamilies = listings
      ? [...new Set(listings.map((l) => (l as Listing & { base_family?: string }).base_family).filter(Boolean))]
      : [];
    const families = seenFamilies.length
      ? seenFamilies
      : (alert.model_filters ?? []).map((mf) => mf.slug).filter(Boolean);
    if (families.length) p.set("models", (families as string[]).join(","));
    if (alert.min_year) p.set("min_year", String(alert.min_year));
    if (alert.max_year) p.set("max_year", String(alert.max_year));
    if (alert.max_km) p.set("max_km", String(alert.max_km));
    if (alert.min_price) p.set("min_price", String(alert.min_price));
    if (alert.max_price) p.set("max_price", String(alert.max_price));
    return `/buscar?${p.toString()}`;
  })() : "/buscar";

  if (sessionLoading || profileLoading) return null;
  if (!session || (!isPaid && !isFree)) return null;

  return (
    <div className="min-h-screen text-foreground">
      <AppNav />

      <main className="mx-auto max-w-6xl px-6 pb-32 pt-6">
        <Link
          to="/alertas"
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Meus alertas
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-foreground sm:text-4xl">
              {alert?.name ?? "Carregando..."}
            </h1>
            {alert && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {(alert.model_filters ?? []).map((mf, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-foreground/80"
                  >
                    {mf.display_name ?? mf.slug}
                  </span>
                ))}
                {alert.min_year && (
                  <span className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-3 py-1 text-xs text-muted-foreground">
                    A partir de {alert.min_year}
                  </span>
                )}
                {alert.max_year && (
                  <span className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-3 py-1 text-xs text-muted-foreground">
                    Até {alert.max_year}
                  </span>
                )}
                {alert.max_km && (
                  <span className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-3 py-1 text-xs text-muted-foreground">
                    Até {fmtBR(alert.max_km)} km
                  </span>
                )}
                {alert.max_price && (
                  <span className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-3 py-1 text-xs text-muted-foreground">
                    Até R$ {fmtBR(alert.max_price)}
                  </span>
                )}
              </div>
            )}
          </div>
          {alert && (
            <Link
              to={buscarUrl as "/buscar"}
              className="btn-primary shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
            >
              Ver anúncios ativos
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {fetchError && (
          <p className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {fetchError}
          </p>
        )}

        {listings === null && !fetchError && (
          <div className="surface-card mt-8 rounded-2xl p-6 text-sm text-muted-foreground">
            Carregando...
          </div>
        )}

        {listings?.length === 0 && (
          <div className="surface-card mt-8 rounded-2xl p-10 text-center">
            <p className="font-display text-lg text-foreground">Nenhum resultado ainda</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Assim que encontrarmos carros que correspondam ao seu alerta, eles aparecerão aqui.
            </p>
          </div>
        )}

        {listings && listings.length > 0 && (
          <>
            {/* Mobile card list */}
            <div className="mt-8 space-y-3 md:hidden">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {(["price", "km", "year", "source"] as SortKey[]).map((key) => {
                  const labels: Record<SortKey, string> = { price: "Preço", km: "KM", year: "Ano", source: "Site" };
                  const active = sortKey === key;
                  return (
                    <button
                      key={key}
                      onClick={() => handleSort(key)}
                      className={`shrink-0 rounded-full border px-3 py-1 text-xs transition ${
                        active
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] text-foreground/70 dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                      }`}
                    >
                      {labels[key]}{active ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                    </button>
                  );
                })}
              </div>
              {sortListings(listings, sortKey, sortDir).map((l) => {
                const gone = l.status === "gone";
                const inComparador = comparadorItems.some((c) => c.id === l.id);
                return (
                  <div
                    key={l.id}
                    onClick={gone ? undefined : () => setSelectedListing(l)}
                    className={`surface-card rounded-2xl p-4 transition dark:border-white/[0.12] border-black/[0.12] ${
                      gone ? "opacity-50" : "cursor-pointer dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">
                          {l.title}
                          <VisitedBadge id={l.id} className="ml-1.5 align-middle" />
                        </p>
                        {gone && (
                          <span className="mt-0.5 inline-block rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-amber-400">
                            Indisponível
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {!gone && (
                          <button
                            onClick={(e) => toggleFavorite(e, l)}
                            title={favoriteIds.has(l.id) ? "Remover dos favoritos" : "Salvar nos favoritos"}
                            className="rounded-full p-1 transition"
                          >
                            <Heart className={`h-5 w-5 transition ${favoriteIds.has(l.id) ? "fill-primary text-primary" : "text-foreground/40 hover:text-foreground/80"}`} />
                          </button>
                        )}
                        <a
                          href={goUrl(l.id, "table")}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-full p-1 text-foreground/40 transition hover:text-foreground/80"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {l.source}{l.year ? ` · ${l.year}` : ""}
                    </p>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="flex gap-4 text-sm">
                        {l.price != null && (
                          <span className="font-medium text-foreground">R$ {fmtBR(l.price)}</span>
                        )}
                        {l.km != null && (
                          <span className="text-muted-foreground">{fmtBR(l.km)} km</span>
                        )}
                      </div>
                      {!gone && (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleComparador(l); }}
                          title={inComparador ? "Remover do comparador" : "Adicionar ao comparador"}
                          className="rounded-full p-1 transition"
                        >
                          {inComparador
                            ? <CheckCircle2 className="h-5 w-5 text-primary" />
                            : <PlusCircle className="h-5 w-5 text-foreground/40" />}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="surface-card mt-8 hidden overflow-hidden rounded-3xl md:block">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead>
                    <tr className="border-b dark:border-white/10 border-black/10 text-left text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      <Th sortKey="source" current={sortKey} dir={sortDir} onClick={handleSort} className="w-48">SITE</Th>
                      <th className="px-5 py-3.5">CARRO</th>
                      <Th sortKey="year" current={sortKey} dir={sortDir} onClick={handleSort} align="right" className="w-20">ANO</Th>
                      <Th sortKey="km" current={sortKey} dir={sortDir} onClick={handleSort} align="right" className="w-28">KM</Th>
                      <Th sortKey="price" current={sortKey} dir={sortDir} onClick={handleSort} align="right" className="w-32">PREÇO</Th>
                      <th className="w-24 px-3 py-3.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortListings(listings, sortKey, sortDir).map((l) => {
                      const gone = l.status === "gone";
                      const inComparador = comparadorItems.some((c) => c.id === l.id);
                      return (
                        <tr
                          key={l.id}
                          onClick={gone ? undefined : () => { posthog.capture("listing_clicked", { context: "radar", source: l.source, price: l.price, year: l.year, model_slug: l.model_slug }); setSelectedListing(l); }}
                          className={`border-b dark:border-white/5 border-black/5 text-foreground/90 transition ${
                            gone ? "opacity-50" : "cursor-pointer dark:hover:bg-white/[0.03] hover:bg-black/[0.03]"
                          }`}
                        >
                          <td className="px-5 py-3.5 text-muted-foreground">{l.source}</td>
                          <td className="px-5 py-3.5 font-medium">
                            {l.title}
                            <VisitedBadge id={l.id} className="ml-2 align-middle" />
                            {gone && (
                              <span className="ml-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-amber-400">
                                Indisponível
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right">{l.year ?? "—"}</td>
                          <td className="px-5 py-3.5 text-right">
                            {l.km != null ? `${fmtBR(l.km)} km` : "—"}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            {l.price != null ? `R$ ${fmtBR(l.price)}` : "—"}
                          </td>
                          <td className="pl-3 pr-1 py-3.5" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {!gone && (
                                <button
                                  onClick={() => toggleComparador(l)}
                                  title={inComparador ? "Remover do comparador" : "Adicionar ao comparador"}
                                  className="rounded-full p-1 transition"
                                >
                                  {inComparador
                                    ? <CheckCircle2 className="h-5 w-5 text-primary" />
                                    : <PlusCircle className="h-5 w-5 text-foreground/40 hover:text-foreground/80" />}
                                </button>
                              )}
                              {!gone && (
                                <button
                                  onClick={(e) => toggleFavorite(e, l)}
                                  title={favoriteIds.has(l.id) ? "Remover dos favoritos" : "Salvar nos favoritos"}
                                  className="rounded-full p-1 transition"
                                >
                                  <Heart className={`h-5 w-5 transition ${favoriteIds.has(l.id) ? "fill-primary text-primary" : "text-foreground/40 hover:text-foreground/80"}`} />
                                </button>
                              )}
                              <a
                                href={goUrl(l.id, "table")}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                title="Abrir anúncio"
                                className="inline-flex rounded-full p-1 text-foreground/40 transition hover:text-foreground/80"
                              >
                                <ExternalLink className="h-5 w-5" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {dispatches !== null && (
          <section className="mt-10">
            <h2 className="font-display text-base text-foreground">Histórico de disparos</h2>
            {dispatches.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Nenhum disparo registrado ainda — o histórico começa a partir do próximo envio.
              </p>
            ) : (
              <div className="mt-3 space-y-0 divide-y dark:divide-white/[0.06] divide-black/[0.06]">
                {dispatches.map((d, i) => {
                  const date = new Date(d.sent_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
                  const isFirst = i === dispatches.length - 1;
                  return (
                    <div key={d.id} className="flex items-center gap-4 py-2.5 text-sm">
                      <span className="w-12 shrink-0 text-xs text-muted-foreground">{date}</span>
                      <span className="flex-1 text-foreground/80">
                        {isFirst ? (
                          <>1º disparo · {d.total_count} anúncios</>
                        ) : (
                          <>
                            {d.new_count > 0 && <span className="text-emerald-500">+{d.new_count} novo{d.new_count !== 1 ? "s" : ""}</span>}
                            {d.new_count > 0 && d.gone_count > 0 && <span className="text-muted-foreground"> · </span>}
                            {d.gone_count > 0 && <span className="text-muted-foreground">{d.gone_count} saiu{d.gone_count !== 1 ? "ram" : ""}</span>}
                          </>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">{d.total_count} total</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {MERCADO_PANEL_ENABLED && alert && session && (
          <MercadoPanel
            modelSlugs={(alert.model_filters ?? []).map((mf) => mf.slug)}
            modelNames={Object.fromEntries(
              (alert.model_filters ?? []).map((mf) => [mf.slug, mf.display_name ?? mf.slug])
            )}
            session={session}
          />
        )}
      </main>

      {comparadorItems.length >= 2 && !comparadorOpen && (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
          <div className="btn-primary inline-flex items-stretch overflow-hidden rounded-full shadow-lg">
            <button
              onClick={() => { posthog.capture("comparador_opened", { context: "radar", listing_count: comparadorItems.length }); setComparadorOpen(true); }}
              className="inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 text-sm font-medium"
            >
              <BarChart2 className="h-4 w-4" />
              Comparar {comparadorItems.length}
            </button>
            <div className="w-px self-stretch bg-white/25 my-1.5" />
            <button
              type="button"
              onClick={() => setComparadorItems([])}
              aria-label="Limpar seleção do comparador"
              className="inline-flex items-center px-3 py-2.5 transition hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selectedListing && (
        <ListingDetailDrawer
          listing={selectedListing}
          session={session}
          isInComparador={comparadorItems.some((c) => c.id === selectedListing.id)}
          onClose={() => setSelectedListing(null)}
          onToggleComparador={(l) => toggleComparador(l)}
          onOpenSimilar={(l) => setSelectedListing(l)}
        />
      )}

      {comparadorOpen && (
        <ComparadorPanel
          listings={comparadorItems}
          session={session}
          onRemove={(id) => {
            setComparadorItems((prev) => prev.filter((l) => l.id !== id));
            if (comparadorItems.length <= 2) setComparadorOpen(false);
          }}
          onClose={() => setComparadorOpen(false)}
        />
      )}
    </div>
  );
}

function Th({
  children,
  sortKey: key,
  current,
  dir,
  onClick,
  align = "left",
  className = "",
}: {
  children: React.ReactNode;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const active = current === key;
  return (
    <th className={`px-5 py-3.5 ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button
        onClick={() => onClick(key)}
        className={`inline-flex items-center gap-1 transition hover:text-foreground ${active ? "text-foreground" : ""}`}
      >
        {children}
        <span className="text-[8px] opacity-60">{active ? (dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}
